import type { ContentField } from "@/types/template";
import type { ParseError } from "@/parser/types";

function parseTextFieldParams(typeStr: string, extraParams: string[]): Partial<ContentField> {
  const optional = typeStr.includes("?");
  const multiline = extraParams.includes("multiline");
  const defaultParam = extraParams.find((p) => p.startsWith("default="));
  const defaultValue = defaultParam
    ? defaultParam.slice("default=".length).replaceAll(String.raw`\n`, "\n")
    : "";
  return { defaultValue, id: "", label: "", multiline, optional, type: "text" };
}

function parseScoreFieldParams(
  extraParams: string[],
  lineNum: number,
  errors: ParseError[],
): Partial<ContentField> | null {
  const rangeStr = extraParams.find((p) => /^\d+-\d+$/.test(p));
  const rangeMatch = rangeStr?.match(/^(\d+)-(\d+)$/);
  if (!rangeMatch) {
    errors.push({
      line: lineNum,
      message: `score field requires a range like "0-4", got "${extraParams[0] ?? ""}"`,
    });
    return null;
  }
  const [, minStr = "", maxStr = ""] = rangeMatch;
  const min = Number.parseInt(minStr);
  const max = Number.parseInt(maxStr);
  const groupParam = extraParams.find((p) => p.startsWith("group="));
  const aggParam = extraParams.find((p) => p.startsWith("agg="));
  const pairId = groupParam ? groupParam.slice("group=".length) : undefined;
  const aggStr = aggParam ? aggParam.slice("agg=".length) : undefined;
  const pairAgg = aggStr === "min" || aggStr === "max" ? aggStr : undefined;
  return { id: "", label: "", max, min, pairAgg, pairId, scoreOptions: undefined, type: "score" };
}

function parseDateFieldParams(typeStr: string, extraParams: string[]): Partial<ContentField> {
  const optional = typeStr.includes("?");
  const defaultParam = extraParams.find((p) => p.startsWith("default="));
  const defaultValue = defaultParam ? defaultParam.slice("default=".length) : "";
  return { defaultValue, id: "", label: "", optional, type: "date" };
}

/**
 * Parse `@field(type, param1, param2)` params into a partial ContentField.
 * The `id` and `label` will be filled in later once the label line is read.
 */
export function parseFieldParams(
  typeStr: string,
  extraParams: string[],
  lineNum: number,
  errors: ParseError[],
): Partial<ContentField> | null {
  if (/^text\??/.test(typeStr)) {
    return parseTextFieldParams(typeStr, extraParams);
  }

  if (typeStr === "select") {
    return { id: "", label: "", options: [], type: "select" };
  }

  if (typeStr === "checklist" || typeStr === "multiselect") {
    const defaultAll = extraParams.includes("defaultAll");
    return {
      id: "",
      items: [],
      label: "",
      type: "checkbox-list",
      ...(defaultAll ? { defaultAll } : {}),
    };
  }

  if (typeStr === "statement-list") {
    return { id: "", items: [], label: "", type: "statement-list" };
  }

  if (typeStr === "score") {
    return parseScoreFieldParams(extraParams, lineNum, errors);
  }

  if (typeStr === "grid") {
    return { columns: [], id: "", label: "", rows: [], type: "grid" };
  }

  if (typeStr === "sub-template") {
    const templateId = extraParams[0];
    if (!templateId) {
      errors.push({ line: lineNum, message: "sub-template field requires a template ID" });
      return null;
    }
    return { id: "", label: "", templateId, type: "sub-template" };
  }

  if (/^date\??$/.test(typeStr)) {
    return parseDateFieldParams(typeStr, extraParams);
  }

  errors.push({ line: lineNum, message: `Unknown field type: ${typeStr}` });
  return null;
}
